<template>
	<article class="d-news-list">
		<header
			v-if="title || name || teaser"
			class="text-white w-full px-layout-margin pt-[35px] mb-[50px]"
		>
			<h1
				v-if="title || name"
				class="text-45 font-light mb-[10px]"
				v-html="title || name"
			></h1>

			<p
				v-if="teaser"
				class="whitespace-pre-line text-22"
				v-html="teaser"
			></p>
		</header>

		<LimboSearch
			v-if="searchForm"
			v-slot="limboSearch"
			role="search"
			:search-filters="searchForm"
			:config="{
				allowSearchFiltersMutation: true,
				clearRouterHashOnSearch: true,
				updateVueRouteOnSearch: true,
			}"
			class=">=568:px-layout-margin"
			@update="updateSearchId"
		>
			<!-- We want the form there semantically, but not functionally -->
			<!-- Ie.: The form itself doesn't actually do anything! -->
			<form
				:action="action"
				:method="searchForm.method"
				class="d-news-list__search-form text-white w-full p-box-padding grid grid-cols-1 gap-[17px]"
				@submit.prevent
			>
				<FilterText
					v-if="searchForm.fieldByName.text"
					v-model="tempTextValue"
					type="search"
					:name="searchForm.fieldByName.text.name"
					:placeholder="searchForm.fieldByName.text.placeholder"
					class="w-full"
					@submit="
						($event) => {
							searchForm.fieldByName.text.value = tempTextValue;
							limboSearch.action.submit($event);
						}
					"
				/>
			</form>

			<section
				aria-live="polite"
				:aria-busy="
					limboSearch.state?.isLoading ||
					!limboSearch.state?.isInitiated
						? 'true'
						: 'false'
				"
				class="relative"
			>
				<!-- Search head -->
				<SearchSpinner class="absolute left-[0px] top-[0px] w-full" />
				<header
					class="w-full px-layout-margin >=568:px-[0px] py-[25px] flex items-center text-white"
				>
					<!-- The search status -->
					<span
						v-if="
							limboSearch.state?.isLoading ||
							!limboSearch.state?.isInitiated
						"
						role="”status”"
						class=""
					>
						Søger...
					</span>
					<span v-else role="”status”" class="">
						Der blev fundet
						<strong
							v-text="limboSearch.pagination?.total ?? 0"
						></strong>
						{{
							limboSearch.pagination?.total === 1
								? 'nyhed'
								: 'nyheder'
						}}
					</span>
				</header>

				<!-- Results -->
				<div class="grid grid-cols-1 gap-layout-gutter w-full">
					<article
						v-for="(item, itemIndex) in limboSearch.data"
						:key="`${item.id}`"
						class="d-news-list__item w-full flex flex-col p-box-padding >=760:pl-[125px] bg-white"
						:class="{
							'd-news-list__item--initial-load':
								!limboSearch.state.isAppend,
						}"
						:style="{
							'--delay': `${Math.min(itemIndex * 25, 1500)}ms`,
						}"
					>
						<!-- Heading -->
						<h2
							v-if="item.name"
							class="text-16 font-bold"
							v-html="item.name"
						></h2>

						<!-- Text -->
						<div
							v-if="item.teaser"
							class="s-rich-text mt-paragraph"
							v-html="item.teaser"
						></div>

						<NuxtLink
							v-if="item.url && item.name"
							:to="item.url"
							class="mt-[30px] inline-block underline text-text hover:text-opacity-80 transition-colors duration-300"
						>
							Læs mere
						</NuxtLink>
					</article>
				</div>

				<!-- Load more -->
				<footer
					v-if="limboSearch.state?.hasMoreItems"
					class="w-full flex justify-center items-center py-[20px]"
				>
					<BaseButton
						:aria-disabled="
							limboSearch.state?.isLoading ||
							!limboSearch.state?.isInitiated
								? 'true'
								: 'false'
						"
						@click="() => limboSearch.action.fetchMore()"
						v-text="'Vis flere'"
					/>
				</footer>
			</section>
		</LimboSearch>
	</article>
</template>

<script>
export default {
	inheritAttrs: false,
};
</script>

<script setup>
import { useLimboForm } from '~/composables/useLimboForm';

const props = defineProps({
	title: String,
	name: String,
	teaser: String,
	forms: Array,
	infoLookupTable: Object,
});

const searchForm = ref(
	useLimboForm(props.forms?.[0], { populateFromQuery: true })
);
const tempTextValue = ref(searchForm.value.fieldByName.text?.value ?? '');
const action = ref(null);
onMounted(() => {
	action.value = searchForm.value.endpointUrl;
});

const searchId = ref(0);
const updateSearchId = (searchInfo) => {
	if (!searchInfo?.state?.isLoading) {
		return ++searchId.value;
	}
};
</script>

<style lang="postcss">
.d-news-list {
	@apply w-full max-w-layout-max pb-[80px] mx-auto;
}

.d-news-list__search-form {
	@apply bg-cyan;
	background-image: linear-gradient(#046c72, #05868e);
}

.d-news-list__tags {
	&:before {
		content: 'Emne: ';
	}
	li:not(:last-child):after {
		content: ', ';
	}
}

.d-news-list__item--initial-load {
	opacity: 0;
	animation: d-news-list__item 300ms var(--delay, 0ms) ease-out forwards;
}
@keyframes d-news-list__item {
	0% {
		opacity: 0;
	}
	100% {
		opacity: 1;
	}
}
</style>
